import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, Mail, Inbox } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('pending'); // pending, verifying, success, error
  const [message, setMessage] = useState('');
  
  const token = searchParams.get('token');

  const verifyEmail = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/verify-email/${token}`);
      setStatus('success');
      setMessage(response.data.message || 'Email verified successfully!');
      
      // Redirect to pricing after 3 seconds
      setTimeout(() => {
        navigate('/pricing');
      }, 3000);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Verification failed. Please try again.');
    }
  }, [navigate, token]);

  useEffect(() => {
    if (token) {
      setStatus('verifying');
      verifyEmail();
    } else {
      setStatus('pending');
    }
  }, [token, verifyEmail]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="card-dashboard text-center p-8">
          {/* Logo */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-2xl text-slate-900">Nexus Leads</span>
          </div>

          {status === 'pending' && (
            <>
              <Inbox className="w-16 h-16 text-indigo-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Check Your Email</h2>
              <p className="text-slate-500 mb-6">
                We've sent a verification link to your email address. Please check your inbox and click the link to verify your account.
              </p>
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-6">
                <p className="text-sm text-amber-800">
                  Don't see the email? Check your spam folder or wait a few minutes.
                </p>
              </div>
              <Button 
                onClick={() => navigate('/login')} 
                variant="outline"
                className="w-full"
              >
                Back to Login
              </Button>
            </>
          )}

          {status === 'verifying' && (
            <>
              <Loader2 className="w-16 h-16 text-indigo-600 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Verifying your email...</h2>
              <p className="text-slate-500">Please wait while we verify your email address.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Email Verified!</h2>
              <p className="text-slate-500 mb-6">{message}</p>
              <p className="text-sm text-indigo-600 mb-4">Redirecting to pricing page...</p>
              <Button 
                onClick={() => navigate('/pricing')} 
                className="btn-primary"
              >
                Choose Your Plan
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Verification Failed</h2>
              <p className="text-slate-500 mb-6">{message}</p>
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/login')} 
                  className="btn-primary w-full"
                >
                  Go to Login
                </Button>
                <Link 
                  to="/register" 
                  className="text-sm text-indigo-600 hover:text-indigo-700 block"
                >
                  Create a new account
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
